<template>
  <div class="google-map">
    <GoogleMap
      id="location-map"
      mapId="location-map"
      :center="mapCenter"
      :zoom="calculateZoom"
      :api-key="GOOGLE_MAPS_API_KEY"
    >
      <AdvancedMarker
        v-for="location in filteredLocations"
        :key="location.id"
        :options="pinOptions(location)"
      >
      </AdvancedMarker>
    </GoogleMap>
  </div>
</template>

<script lang="ts" setup>

import { GoogleMap, AdvancedMarker } from 'vue3-google-map'
import { ref, computed, onMounted, watch } from 'vue';
import type { CompanyLocation } from '@/types';

const config = useRuntimeConfig();

const props = defineProps<{
  locations: typeof CompanyLocation[]
}>();

const mapCenter = ref({ lat: 0, lng: 0 });
const bounds = ref(null);
const GOOGLE_MAPS_API_KEY = config.public.GOOGLE_MAPS_API_KEY;
const filteredLocations = ref<typeof CompanyLocation[]>(props.locations);

const parseCoordinates = (coordinates: string | null): { lat: number, lng: number } => {
  if (!coordinates) {
    return { lat: 0, lng: 0 };
  }

  const [lat, lng] = coordinates.split(',').map(Number);
  return { lat, lng };
};

// https://vue3-google-map.com/components/advanced-marker.html
// https://developers.google.com/maps/documentation/javascript/reference/advanced-markers?hl=de#AdvancedMarkerElementOptions
const pinOptions = (location: typeof CompanyLocation) => {
  return {
    title: location.name,
    content: createMarkerContent(location),
    position: parseCoordinates(location.coordinates),
  }
}

const createMarkerContent = (location: typeof CompanyLocation) => {
  const div = document.createElement('div');
  div.className = 'custom-marker';
  div.style.position = 'relative';

  const pin = document.createElement('div');
  pin.className = 'pin';
  pin.style.width = '40px';
  pin.style.height = '50px';
  pin.style.backgroundColor = '#235789';
  pin.style.borderRadius = '50% 50% 50% 0';
  pin.style.transform = 'rotate(-45deg)';

  const rating = document.createElement('div');
  rating.className = 'rating';
  rating.style.position = 'absolute';
  rating.style.top = '50%';
  rating.style.left = '50%';
  rating.style.transform = 'translate(-50%, -50%) rotate(45deg)';
  rating.style.color = '#FFFFFF';
  rating.style.fontSize = '16px';
  rating.textContent = location.settings.rating?.toFixed(1);

  const star = document.createElement('div');
  star.className = 'star';
  star.style.position = 'absolute';
  star.style.bottom = '2px';
  star.style.left = '25%';
  star.style.transform = 'translateX(-50%) rotate(45deg)';
  star.style.color = '#FFFFFF';
  star.style.fontSize = '12px';
  star.textContent = '★';

  pin.appendChild(rating);
  pin.appendChild(star);
  div.appendChild(pin);

  // if (location.isExpanded) {
  //   const info = document.createElement('div');
  //   info.className = 'info';
  //   info.style.backgroundColor = 'rgba(255, 255, 255, 0.9)';
  //   info.style.padding = '5px';
  //   info.style.borderRadius = '4px';
  //   info.style.maxWidth = '180px';
  //   info.style.wordWrap = 'break-word';
  //   info.textContent = location.name;

  //   div.appendChild(info);
  // }

  return div;
};

const calculateCenter = () => {
  if (filteredLocations.value.length === 0) return { lat: 0, lng: 0 };

  const totalLat = filteredLocations.value.reduce((sum, loc) => sum + parseCoordinates(loc.coordinates).lat, 0);
  const totalLng = filteredLocations.value.reduce((sum, loc) => sum + parseCoordinates(loc.coordinates).lng, 0);

  return {
    lat: totalLat / filteredLocations.value.length,
    lng: totalLng / filteredLocations.value.length
  };
};

const calculateBounds = () => {
  if (filteredLocations.value.length === 0) return null;

  const bounds = new google.maps.LatLngBounds();
  filteredLocations.value.forEach(location => {
    const { lat, lng } = parseCoordinates(location.coordinates);
    bounds.extend(new google.maps.LatLng(lat * 1.5, lng * 1.5));
  });

  return bounds;
};

const calculateZoom = computed(() => {
  if (!bounds.value) return 12;

  const GLOBE_WIDTH = 256;
  const west = bounds.value.getSouthWest().lng();
  const east = bounds.value.getNorthEast().lng();
  let angle = east - west;
  if (angle < 0) {
    angle += 360;
  }
  const mapWidth = 600;
  const zoom = Math.round(Math.log(mapWidth * 360 / angle / GLOBE_WIDTH) / Math.LN2);
  return Math.min(zoom, 15);
});

onMounted(() => {
  filteredLocations.value = filteredLocations.value.filter(loc => loc.coordinates);

  setTimeout(() => {
    mapCenter.value = calculateCenter();
    bounds.value = calculateBounds();

    watch(() => filteredLocations.value, () => {
      mapCenter.value = calculateCenter();
      bounds.value = calculateBounds();
    }, { deep: true });
  }, 1000);
});
</script>

<style scoped lang="scss">
:deep(#location-map) {
  height: 50vh;
  min-height: 400px;
  width: 100%;
}

.custom-marker {
  cursor: pointer;
}
</style>